/**
 * Error store for managing API error history.
 * Provides global state for capturing, displaying, and persisting errors.
 *
 * Features:
 * - Stores up to MAX_ERRORS errors (auto-prunes oldest)
 * - Persists to localStorage across sessions
 * - Tracks read/unread status for badge count
 *
 * @module stores/useErrorStore
 */

import { create } from 'zustand';

/**
 * Maximum number of errors to store.
 * Oldest errors are automatically removed when this limit is exceeded.
 */
const MAX_ERRORS = 50;

/**
 * localStorage key for persisting errors
 */
const STORAGE_KEY = 'adc_error_history';

/**
 * Represents a captured API error with debugging details.
 */
export interface AppError {
  /** Unique identifier for this error */
  id: string;
  /** ISO timestamp when the error occurred */
  timestamp: string;
  /** Error title/summary message */
  message: string;
  /** Detailed error description (optional) */
  detail?: string;
  /** HTTP status code (e.g., 500, 400) */
  status?: number;
  /** Custom API error code */
  errorCode?: number;
  /** Distributed tracing ID for debugging */
  traceId?: string;
  /** Request ID for support lookup */
  requestId?: string;
  /** Whether the user has viewed this error */
  isRead: boolean;
  /** The API endpoint that failed */
  url?: string;
}

/**
 * Input for adding a new error (id, timestamp, isRead are auto-generated)
 */
export type AddErrorInput = Omit<AppError, 'id' | 'timestamp' | 'isRead'>;

/**
 * Error store state and actions
 */
interface ErrorStore {
  /** List of captured errors, newest first */
  errors: AppError[];
  /** Count of unread errors for badge display */
  unreadCount: number;
  /** Whether the error drawer is currently open */
  isDrawerOpen: boolean;

  /**
   * Add a new error to the store.
   * Automatically generates id and timestamp.
   * @returns The generated error ID
   */
  addError: (error: AddErrorInput) => string;

  /**
   * Remove a specific error by ID
   */
  removeError: (id: string) => void;

  /**
   * Clear all errors from the store
   */
  clearAllErrors: () => void;

  /**
   * Mark a specific error as read
   */
  markAsRead: (id: string) => void;

  /**
   * Mark all errors as read
   */
  markAllAsRead: () => void;

  /**
   * Open the error drawer
   */
  openDrawer: () => void;

  /**
   * Close the error drawer
   */
  closeDrawer: () => void;

  /**
   * Toggle the error drawer open/closed
   */
  toggleDrawer: () => void;
}

/**
 * Generate a unique ID for an error
 */
function generateId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a fingerprint for an error to identify duplicates.
 * Errors with the same fingerprint are considered identical.
 */
function createErrorFingerprint(error: Omit<AppError, 'id' | 'timestamp' | 'isRead'>): string {
  return JSON.stringify({
    status: error.status,
    url: error.url,
    message: error.message,
    errorCode: error.errorCode,
  });
}

/**
 * Load errors from localStorage
 */
function loadFromStorage(): AppError[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('[ErrorStore] Failed to load errors from localStorage:', e);
  }
  return [];
}

/**
 * Save errors to localStorage
 */
function saveToStorage(errors: AppError[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(errors));
  } catch (e) {
    console.warn('[ErrorStore] Failed to save errors to localStorage:', e);
  }
}

/**
 * Calculate unread count from errors array
 */
function calculateUnreadCount(errors: AppError[]): number {
  return errors.filter((e) => !e.isRead).length;
}

/**
 * Global error store for managing API error history.
 *
 * @example
 * // Add an error
 * useErrorStore.getState().addError({
 *   message: 'Failed to create sandbox',
 *   status: 500,
 *   requestId: 'abc-123',
 *   traceId: 'xyz-789',
 * });
 *
 * // In a component
 * const { errors, unreadCount, openDrawer } = useErrorStore();
 */
export const useErrorStore = create<ErrorStore>((set, get) => {
  // Initialize with stored errors
  const initialErrors = loadFromStorage();

  return {
    errors: initialErrors,
    unreadCount: calculateUnreadCount(initialErrors),
    isDrawerOpen: false,

    addError: (errorInput) => {
      const fingerprint = createErrorFingerprint(errorInput);

      // Check for existing duplicate - if found, refresh its timestamp and move to top
      const existingError = get().errors.find((e) => createErrorFingerprint(e) === fingerprint);
      if (existingError) {
        set((state) => {
          const refreshed: AppError = {
            ...existingError,
            ...errorInput,
            id: existingError.id,
            timestamp: new Date().toISOString(),
            isRead: false,
          };
          const updatedErrors = [
            refreshed,
            ...state.errors.filter((e) => e.id !== existingError.id),
          ];
          saveToStorage(updatedErrors);
          return {
            errors: updatedErrors,
            unreadCount: calculateUnreadCount(updatedErrors),
          };
        });
        return existingError.id;
      }

      const id = generateId();
      const newError: AppError = {
        ...errorInput,
        id,
        timestamp: new Date().toISOString(),
        isRead: false,
      };

      set((state) => {
        // Add new error at the beginning (newest first)
        const updatedErrors = [newError, ...state.errors];

        // Prune to max limit
        const prunedErrors = updatedErrors.slice(0, MAX_ERRORS);

        // Persist to storage
        saveToStorage(prunedErrors);

        return {
          errors: prunedErrors,
          unreadCount: calculateUnreadCount(prunedErrors),
        };
      });

      return id;
    },

    removeError: (id) => {
      set((state) => {
        const updatedErrors = state.errors.filter((e) => e.id !== id);
        saveToStorage(updatedErrors);
        return {
          errors: updatedErrors,
          unreadCount: calculateUnreadCount(updatedErrors),
        };
      });
    },

    clearAllErrors: () => {
      saveToStorage([]);
      set({ errors: [], unreadCount: 0 });
    },

    markAsRead: (id) => {
      set((state) => {
        const updatedErrors = state.errors.map((e) => (e.id === id ? { ...e, isRead: true } : e));
        saveToStorage(updatedErrors);
        return {
          errors: updatedErrors,
          unreadCount: calculateUnreadCount(updatedErrors),
        };
      });
    },

    markAllAsRead: () => {
      set((state) => {
        const updatedErrors = state.errors.map((e) => ({ ...e, isRead: true }));
        saveToStorage(updatedErrors);
        return {
          errors: updatedErrors,
          unreadCount: 0,
        };
      });
    },

    openDrawer: () => {
      // Mark all as read when opening drawer
      const state = get();
      if (state.unreadCount > 0) {
        const updatedErrors = state.errors.map((e) => ({ ...e, isRead: true }));
        saveToStorage(updatedErrors);
        set({ isDrawerOpen: true, errors: updatedErrors, unreadCount: 0 });
      } else {
        set({ isDrawerOpen: true });
      }
    },

    closeDrawer: () => {
      set({ isDrawerOpen: false });
    },

    toggleDrawer: () => {
      const isOpen = get().isDrawerOpen;
      if (isOpen) {
        get().closeDrawer();
      } else {
        get().openDrawer();
      }
    },
  };
});

/**
 * Format an error for copying to clipboard.
 * Creates a human-readable report for sharing with support.
 */
export function formatErrorForClipboard(error: AppError): string {
  const lines: string[] = ['── Error Report ──'];

  // Format timestamp
  const date = new Date(error.timestamp);
  lines.push(`Time: ${date.toISOString().replace('T', ' ').replace('Z', ' UTC')}`);

  lines.push(`Error: ${error.message}`);

  if (error.status !== undefined) {
    lines.push(`Status: ${error.status}`);
  }

  if (error.errorCode !== undefined) {
    lines.push(`Error Code: ${error.errorCode}`);
  }

  if (error.requestId) {
    lines.push(`Request ID: ${error.requestId}`);
  }

  if (error.traceId) {
    lines.push(`Trace ID: ${error.traceId}`);
  }

  if (error.url) {
    lines.push(`URL: ${error.url}`);
  }

  if (error.detail) {
    lines.push(`Detail: ${error.detail}`);
  }

  return lines.join('\n');
}
